/* eslint-disable @typescript-eslint/no-explicit-any */
import cloneDeep from 'lodash.clonedeep';
import findIndex from 'lodash.findindex';
import orderBy from 'lodash.orderby';
import uniq from 'lodash.uniq';
import Immutable, { ImmutableObject } from 'seamless-immutable';
import categoryModel from '../models/category';

import moment from 'moment';
import * as types from '../constants/actionTypes';
import { CATEGORY_ID } from '../constants/category';
import {
  CONVERSATION_STATUS,
  DEFAULT_FOLDER_OPTION,
  GiftLevel,
  MEMBER_LEVEL,
  MEMBER_TYPES,
  PRIMARY_FILTER,
  SECONDARY_FILTER,
} from '../constants/constants';
import { TYPE_GIFT } from '../constants/messageTypes';
import * as messageUtils from '../functions/messageUtils';
import { isGroupMessageStatus } from '../functions/messageUtils';
import * as untils from '../functions/utils';
import { sortConversationByMessageTimeArray } from '../selectors/conversationSelector';
import * as myStore from '../store/myStore';
import initialState from './initialState';
import Conversation, { IPeerVcard, ISearchConversation, ISelectedConversationInfo } from 'redhotpie-shared-lib/models/conversation';
import { BaseFilter } from '../models/memberSearch';
import User from 'redhotpie-shared-lib/models/user';
import ChatMessage from 'redhotpie-shared-lib/models/chatMessage';
import ConversationStatus from 'redhotpie-shared-lib/models/conversationStatus';

type SortConversationsResult = {
  data: Conversation[];
  pinnedConversations: Conversation[];
  diamondConversations: Conversation[];
  unPinnedConversations: Conversation[];
  unreadCount: number;
  unreadMatchingConversations: number;
  pinMatchCount: number;
};

type ConversationLocationMap = Record<string | number, any>;

type ConversationsState = {
  data: Conversation[];
  showAlertFilter: boolean;
  chatServerTime: number;
  isAutoCallMessageAuthor: boolean;
  removingConversationData: any;
  groupChatShouldBeSelected: string;
  privateRoomJids: string[];
  roomJidsBeingJoined: string[];
  roomJidsJoinedSuccess: string[];
  roomJidsJoinedError: string[];
  lastUpdateConversationsDataTime: number;
  listConversationIdsDeleted: number[];
  isSelectingPeerVCard: boolean;
  shouldSortData: boolean;
  isAutoOpenMessengerSetting: boolean;
  conversationsLastSync: number;
  lastSyncTime: number;
  isRetry: boolean;
  isGetOrCreateConversationLoading: boolean;
  isGetOrCreateConversationError: boolean;
  statusCodeGetOrCreateConversationError: number;
  isConversationsError: boolean;
  isConversationsLoading: boolean;
  isConversationsLoadingNew: boolean;
  isConversationsPinedError: boolean;
  isConversationsPinedLoading: boolean;
  conversationDetailError: boolean;
  conversationDetailLoading: boolean;
  selectedPeerVcard?: IPeerVcard;
  pinListLength: number;
  pinMatchCount: number;
  isAPIFullDataLoaded: boolean;
  isAPIDataPinLoaded: boolean;
  search: ISearchConversation;
  unreadConversations: number;
  unreadMatchingConversations: number;
  isSetReadConversationError: boolean;
  isSetReadConversationLoading: boolean;
  selectedConversationInfo: ISelectedConversationInfo;
  showDelete: boolean;
  uploading: any[];
  pageNumber: number;
  showUnreadCount: boolean;
  userLastMessageTime: number | null;
  buddyLastMessageTime: number | null;
  isShowChatModal: boolean;
  isMemberSearch: boolean;
  dataPreVCard: Record<string, any>;
  isStatementShown: boolean;
  isLoggingEnabled: boolean;
  listMsgDraft: any[];
  isAutoSelectFirstConversation: boolean;
  filters: BaseFilter;
  defaultFilters: BaseFilter;
  isShowConRefineModal: boolean;
  location: any[];
  filteredLocation: any[];
  locationState: any[];
  countrySelected: any;
  conversationLocation: any;
  stateSelected: any;
};

interface IAction {
  type: string;
  categoryId?: string;
  dataKey?: string[];
  payload: any;
  data: any;
  message?: ChatMessage;
  id?: number;
  convId?: number;
  userId?: number;
  count?: number;
  isIgnored?: boolean;
  toggle?: boolean;
  isGroup?: boolean;
  matchingUsers?: User[];
  peerXmppId?: string;
  mid?: string;
  isStatementShown?: boolean;
  isLoggingEnabled?: boolean;
  params?: any;
  chatMessage: ChatMessage;
  selectedPeerVcard: IPeerVcard;
  roomJid?: string;
  peerXpppId?: string;
  unreadCountIncrement?: number;
  isForwardedMessage?: boolean;
  xmppId?: string;
  isEdit?: boolean;
  msgId?: string;
  curNode?: string;
  isFavoritedMutual?: boolean;
  isLikedMutual?: boolean;
  roomJids?: string[];
}

const conversationsInitialState: ImmutableObject<ConversationsState> = Immutable({
  showAlertFilter: false,
  chatServerTime: 0,
  isAutoCallMessageAuthor: false,
  removingConversationData: null,
  groupChatShouldBeSelected: '',
  privateRoomJids: [],
  roomJidsBeingJoined: [],
  roomJidsJoinedSuccess: [],
  roomJidsJoinedError: [],
  lastUpdateConversationsDataTime: 0,
  listConversationIdsDeleted: [],
  isSelectingPeerVCard: false,
  shouldSortData: false,
  isAutoOpenMessengerSetting: false,
  conversationsLastSync: 0,
  lastSyncTime: 0,
  isRetry: false,
  isGetOrCreateConversationLoading: false,
  isGetOrCreateConversationError: false,
  statusCodeGetOrCreateConversationError: 200,
  isConversationsError: false,
  isConversationsLoading: false,
  isConversationsLoadingNew: false,
  isConversationsPinedError: false,
  isConversationsPinedLoading: false,
  conversationDetailError: false,
  conversationDetailLoading: false,
  selectedPeerVcard: undefined,
  data: [],
  dataUnread: {},
  pinListLength: 0,
  pinMatchCount: 0,
  isAPIFullDataLoaded: false,
  isAPIDataPinLoaded: false,
  search: {
    keyword: '',
    primaryIndex: PRIMARY_FILTER.ALL,
    secondaryIndex: SECONDARY_FILTER.ALL_MESSAGES,
    isSearched: false,
  },
  unreadConversations: 0,
  unreadMatchingConversations: 0,
  isSetReadConversationError: false,
  isSetReadConversationLoading: false,
  selectedConversationInfo: {
    diffTime: 0,
    diffTimeGroup: 0,
    isAPIDataLoaded: false,
    isLoading: false,
    isError: false,
    pinned: false,
    matched: false,
    editorStatus: CONVERSATION_STATUS.ERROR_OCCUR, // to resolve RVC-3773
    remainingMessage: 0,
    remainingCredits: 0,
    canVideoCall: true,
    canSend: false,
    canSendPrivate: true,
    canSendPhotos: true,
    id: null,
    memberLevelUserLogin: 0,
    isRecvMatching: false,
    isSenderMatching: false,
    isCreditSend: false,
    timeToReset: 0,
    upgradeAction: 'None',
    conversationKey: '',
    timeUpdateConversationStatus: +new Date(),
    canSendGifts: false,
    msgIdSelected: null,
    isEditMessage: false,
    status: CONVERSATION_STATUS.ERROR_OCCUR,
    remaining: 0,
    reason: '',
    receiverMemberLevel: MEMBER_TYPES.GUEST,
    messageBanExpiry: 0,
    sendPhotosBanExpiry: 0,
    recvMemberLevel: MEMBER_TYPES.GUEST,
  },
  showDelete: false,
  uploading: [],
  pageNumber: 1,
  showUnreadCount: false,
  userLastMessageTime: null,
  buddyLastMessageTime: null,
  isShowChatModal: false,
  isMemberSearch: false,
  dataPreVCard: {},
  isStatementShown: false,
  isLoggingEnabled: false,
  listMsgDraft: [],
  isAutoSelectFirstConversation: false,
  filters: {
    minAge: 18,
    maxAge: 99,
    location: undefined,
    showMeMemberType: new categoryModel({
      id: CATEGORY_ID.MS_SHOW_ME_MEMBER_TYPE,
    }),
    isVerified: false,
    isHasChangeFilter: false,
  },
  defaultFilters: {
    minAge: 18,
    maxAge: 99,
    location: undefined,
    showMeMemberType: new categoryModel({
      id: CATEGORY_ID.MS_SHOW_ME_MEMBER_TYPE,
    }),
    isVerified: false,
    isHasChangeFilter: false,
  },
  isShowConRefineModal: false,
  location: [],
  filteredLocation: [],
  locationState: [],
  countrySelected: null,
  conversationLocation: null,
  stateSelected: null,
});

// eslint-disable-next-line consistent-return
export default function conversationsReducer(state = conversationsInitialState, action: IAction): ImmutableObject<ConversationsState> {
  switch (action.type) {
    // update folders field without re-call get list conversation API
    case types.ADD_CONVERSATION_TO_FOLDER_SUCCESS:
      return state
        .set(
          'data',
          Immutable.asMutable(state).data.map(conversation => {
            if (conversation.id === action.payload.conversationId) {
              return {
                ...conversation,
                folders: uniq([...Immutable.asMutable(conversation.folders), action.payload.folderId]),
                isInboxRetention: action.payload.isInboxRetention || false,
              };
            }
            return conversation;
          })
        )
        .set('lastUpdateConversationsDataTime', new Date().getTime());
    // update folders field without re-call get list conversation API
    case types.REMOVE_CONVERSATION_FROM_FOLDER_SUCCESS:
      return state
        .set(
          'data',
          state.data.map(conversation => {
            if (conversation.id === action.payload.conversationId) {
              return {
                ...conversation,
                folders: Immutable.asMutable(conversation.folders).filter(folderId => folderId !== action.payload.folderId),
                isInboxRetention: true,
              };
            }
            return conversation;
          })
        )
        .set('lastUpdateConversationsDataTime', new Date().getTime());
    case types.CREATE_CONVERSATION_SUCCESS: {
      const existingConversation = state.data.filter(x => x.peerVcard && x.peerVcard.xmppId === action.data.peerVcard.xmppId);
      const newObj = Object.assign({}, { folders: [] }, action.data);
      let updatedData: Conversation[] = [...Immutable.asMutable(state.data)];
      if (existingConversation && existingConversation.length > 0) {
        updatedData = updatedData.map(c => {
          if (c.id === newObj.id) {
            return newObj;
          }
          return c;
        });
      } else {
        updatedData.push(newObj);
      }
      const newConversation =
        newObj.message.msgId || newObj.isGroup
          ? sortConversations(updatedData, action.matchingUsers || [], state.chatServerTime).data
          : updatedData;
      return state
        .set('isRetry', false)
        .set('data', newConversation) // RVC-3797 [Bug][ReactJS Mobile + Desktop][Messenger] Conversations are not date sorted in inbox list
        .set('isSetReadConversationError', false)
        .set('isSetReadConversationLoading', false)
        .set('isGetOrCreateConversationLoading', false)
        .set('statusCodeGetOrCreateConversationError', 200)
        .set('isGetOrCreateConversationError', false)
        .set('lastUpdateConversationsDataTime', new Date().getTime())
        .set('conversationLocation', countAllLocationOfConversation(newConversation));
    }
    case types.RESET_GET_OR_CREATE_ERROR:
      return state.set('statusCodeGetOrCreateConversationError', 200).set('isGetOrCreateConversationError', false);
    case types.CREATE_CONVERSATION_REQUEST:
      return state.set('isGetOrCreateConversationLoading', true);
    case types.CREATE_CONVERSATION_FAILURE:
      return (
        state
          .set('isGetOrCreateConversationError', true)
          // .set('statusCodeGetOrCreateConversationError', action.params)
          .set('isGetOrCreateConversationLoading', false)
      );
    case types.LOAD_CONVERSATION_DETAIL_REQUEST: {
      if (action.peerXmppId) {
        state = state.setIn(['selectedConversationInfo', 'isAPIDataLoaded'], false);
      }
      return state;
    }
    case types.LOAD_CONVERSATION_DETAIL_REQUEST_SUCCESS: {
      state = state.set('conversationDetailError', false).set('conversationDetailLoading', false);
      return state.setIn(['selectedConversationInfo', 'isAPIDataLoaded'], true);
    }
    case types.LOAD_CONVERSATION_DETAIL_FAILURE:
      return state.set('conversationDetailError', true).set('conversationDetailLoading', false);

    case types.LOAD_CONVERSATION_DETAIL_PENDING:
      return state.set('conversationDetailLoading', true);

    case types.CHAT_UPDATE_LAST_MESSAGE_CONVERSATION_READ: {
      const indexItemConversations = state.data.findIndex(conv => conv.message.id === action.mid);
      if (indexItemConversations !== -1) {
        return state
          .setIn(['data', indexItemConversations, 'message', 'read'], true)
          .set('lastUpdateConversationsDataTime', new Date().getTime());
      }
      return state;
    }

    case types.UPDATE_DIFF_CONVERSATIONS_SUCCESS: {
      let newUnpinnedConversations = [];
      let newPinnedConversations = [];
      const newGiftConversations = [];
      let updatedConversations = [];
      const oldConversations = Immutable.asMutable(state.data);
      const newConversations = action.data;
      let diamondConversations = [];
      let unreadConversations = 0;
      let unreadMatchingConversations = 0;
      let pinMatchCount = 0;

      if (oldConversations && oldConversations.length > 0) {
        for (let i = 0; i < oldConversations.length; i++) {
          const searchIndex = newConversations.find((x: Conversation) => x.peerVcard.xmppId == oldConversations[i].peerVcard.xmppId);
          if (!searchIndex) {
            if (oldConversations[i].unreadCount > 0 && !oldConversations[i].isIgnored) {
              unreadConversations++;
              if (oldConversations[i].peerVcard.isMatch) {
                unreadMatchingConversations++;
              }
            }
            if (oldConversations[i].pinned) {
              if (oldConversations[i].peerVcard.isMatch) {
                pinMatchCount++;
              }
              newPinnedConversations.push(oldConversations[i]);
            } else if (
              oldConversations[i]?.hasUnreadGifts ||
              oldConversations[i]?.buddyBoostExpiryAt >= new Date().getTime() + state.chatServerTime
            ) {
              newGiftConversations.push(oldConversations[i]);
            } else if (isDiamondConversationShouldBeSort(oldConversations[i])) {
              diamondConversations.push(oldConversations[i]);
            } else {
              newUnpinnedConversations.push(oldConversations[i]);
            }
          }
        }
      }
      if (newConversations && newConversations.length > 0) {
        for (let i = newConversations.length - 1; i >= 0; i--) {
          if (newConversations[i]) {
            if (newConversations[i].unreadCount > 0 && !newConversations[i].isIgnored) {
              unreadConversations++;
              if (newConversations[i].peerVcard.isMatch) {
                unreadMatchingConversations++;
              }
            }

            if (newConversations[i].pinned) {
              if (newConversations[i].peerVcard.isMatch) {
                pinMatchCount++;
              }
              newPinnedConversations.unshift(newConversations[i]);
            } else if (
              newConversations[i]?.hasUnreadGifts ||
              newConversations[i]?.buddyBoostExpiryAt >= new Date().getTime() + state.chatServerTime
            ) {
              newGiftConversations.unshift(newConversations[i]);
            } else if (isDiamondConversationShouldBeSort(newConversations[i])) {
              diamondConversations.unshift(newConversations[i]);
            } else {
              newUnpinnedConversations.unshift(newConversations[i]);
            }
          }
        }
      }
      if (state.shouldSortData) {
        newPinnedConversations = orderBy(newPinnedConversations, c => c.timestamp, 'desc');
        diamondConversations = orderBy(diamondConversations, c => c.timestamp, 'desc');
        newUnpinnedConversations = orderBy(newUnpinnedConversations, c => c.timestamp, 'desc');
      }
      updatedConversations = [
        ...newPinnedConversations,
        ...sortGiftConversations(newGiftConversations),
        ...diamondConversations,
        ...newUnpinnedConversations,
      ];
      return state
        .set('data', updatedConversations)
        .set('shouldSortData', false)
        .set('conversationDetailError', false)
        .set('conversationDetailLoading', false)
        .set('unreadConversations', unreadConversations)
        .set('unreadMatchingConversations', unreadMatchingConversations)
        .set('pinMatchCount', pinMatchCount)
        .set('lastUpdateConversationsDataTime', new Date().getTime())
        .set('conversationLocation', countAllLocationOfConversation(updatedConversations));
    }

    case types.LOAD_CONVERSATIONS_SUCCESS: {
      let newState = state;
      const newData = sortConversations(action.data, action.matchingUsers || [], state.chatServerTime);
      newState = newState.setIn(['isAPIFullDataLoaded'], true);
      newState = newState.set('data', newData.data);
      if (newData.pinnedConversations) {
        newState = newState.setIn(['pinListLength'], newData.pinnedConversations.length);
      }

      newState = newState
        .set('unreadConversations', newData.unreadCount)
        .set('unreadMatchingConversations', newData.unreadMatchingConversations)
        .set('isConversationsError', false)
        .set('conversationsLastSync', +new Date())
        .set('isConversationsLoading', false)
        .set('pinMatchCount', newData.pinMatchCount)
        .set('isStatementShown', action.isStatementShown)
        .set('isLoggingEnabled', !!action.isLoggingEnabled)
        .set('lastUpdateConversationsDataTime', new Date().getTime())
        .set('conversationLocation', countAllLocationOfConversation(newData.data));
      return newState;
    }
    case types.HIDDEN_STATEMENT_READ:
      return state.set('isStatementShown', false);

    case types.LOAD_NEW_CONVERSATIONS_SUCCESS:
      return state
        .set('isConversationsLoadingNew', false)
        .set('conversationsLastSync', +new Date())
        .set('isConversationsError', false)
        .set('conversationDetailLoading', false)
        .set('lastUpdateConversationsDataTime', new Date().getTime());
    case types.LOAD_CONVERSATIONS_REQUEST:
      return state.set('isConversationsLoading', true);

    case types.LOAD_CONVERSATIONS_FAILURE:
      state = state.set('isRetry', false);
      if (action.params && action.params.isGetFullConversations) {
        state = state.set('isAPIFullDataLoaded', true);
      }
      return state.set('isConversationsError', true).set('isConversationsLoading', false);

    case types.DELETE_CONVERSATION_SUCCESS: {
      const { isGroup, isPin, xmppId, remove } = action.data;
      const pinListLength = isPin ? state.pinListLength - 1 : state.pinListLength;
      const item = state.data.find(conversation => `${conversation.peerVcard.xmppId}` === `${xmppId}`);
      let { unreadConversations, unreadMatchingConversations, pinMatchCount } = state;
      if (item && item.pinned && pinMatchCount > 0 && !isGroup) {
        pinMatchCount -= 1;
      }
      if (unreadConversations > 0 && item) {
        if (item.unreadCount > 0) {
          unreadConversations -= 1;
          if (item && item.peerVcard && item.peerVcard.isMatch && unreadMatchingConversations > 0 && !isGroup) {
            unreadMatchingConversations -= 1;
          }
        }
      }
      const newData = [];
      for (let i = 0; i < state.data.length; i++) {
        const conv = state.data[i];
        if (conv.isGroup && !remove) {
          if (`${conv.peerVcard.xmppId}` === `${xmppId}`) {
            newData.push({
              ...conv,
              isDelete: true,
              pinned: false,
              unreadCount: 0,
              folders: [],
            });
          } else {
            newData.push(conv);
          }
        } else {
          if (`${conv.peerVcard.xmppId}` !== `${xmppId}`) {
            newData.push(conv);
          }
        }
      }
      return state.merge({
        ...state,
        data: newData,
        unreadConversations,
        unreadMatchingConversations,
        pinMatchCount,
        lastUpdateConversationsDataTime: new Date().getTime(),
        pinListLength,
      });
    }

    case types.RETRYING_API_FAIL: {
      return state.set('isRetry', true);
    }

    case types.RETRYING_API_FAIL_SUCCESS: {
      return state.set('isRetry', false);
    }

    case types.SELECT_PEERVCARD_REQUEST:
      return state.set('isSelectingPeerVCard', true);

    case types.SELECT_PEERVCARD_FAILED:
      return state.set('isSelectingPeerVCard', false);

    case types.UPDATE_IS_ACTIVE_PRIVATE_ROOM_CONVERSATION: {
      const index = state.data.findIndex(c => c.peerVcard && c.peerVcard.xmppId === action.roomJid);
      const conversation = state.data[index];
      if (conversation && conversation.peerVcard) {
        state = state.setIn(['data', index, 'isActive'], true);
        state = state.setIn(['data', index, 'isNewInvitation'], false);
      }
      return state.set('lastUpdateConversationsDataTime', +new Date());
    }

    case types.SELECT_PEERVCARD_SUCCESS: {
      const index = untils.getConversationInfo(action.selectedPeerVcard.xmppId, state.data);
      if (index === null) return state;
      state = state.set('selectedPeerVcard', action.selectedPeerVcard);
      // state = state.set('selectedPeerVcard', {...action.selectedPeerVcard, ...state.selectedPeerVcard});
      const selectedConversation = Immutable.asMutable(state.data)[index];
      if (selectedConversation) {
        state = state.setIn(['selectedConversationInfo', 'pinned'], selectedConversation.pinned);
        state = state.setIn(['selectedConversationInfo', 'isSetPeerVCard'], true);
        state = state.setIn(['selectedConversationInfo', 'id'], selectedConversation.id);
        state = state.setIn(['selectedConversationInfo', 'isDelete'], selectedConversation.isDelete);
        if (selectedConversation.isGroup) {
          state = state.setIn(['selectedConversationInfo', 'canSend'], !selectedConversation.isEnded && !selectedConversation.isExpired);
          state = state.setIn(['data', `${index}`], { ...state.data[index], isNewInvitation: false });
        }
      } else {
        state = state.setIn(['selectedConversationInfo', 'isSetPeerVCard'], false);
      }
      if (selectedConversation?.unreadCount > 0 || selectedConversation?.hasUnreadGifts) {
        state = state.set('shouldSortData', true);
      }
      return state.set('indexConversation', index).set('isSelectingPeerVCard', false);
    }

    // case types.GET_MAIN_PHOTO_SUCCESS: {
    //   let mainPhoto = action.data;
    //   let items = state.data.map(value => {
    //     if (value.peerVcard.userId === mainPhoto.userId) {
    //       let updatedConversation = cloneDeep(value);
    //       if (mainPhoto.userId) {
    //         let peerVCard = cloneDeep(value.peerVcard);
    //         peerVCard.avatarUrl = mainPhoto.data.thumbUrl;
    //         updatedConversation.peerVcard = peerVCard;
    //       }
    //       return updatedConversation;
    //     }
    //     else return value;
    //   });
    //   return state.set('data', items);
    // }

    case types.RESET_SELECTED_PEERVCARD_AND_CONVERSATION_INFO_SUCCESS: {
      // let data = [...state.data];
      // if(data[data.length -1 ] && data[data.length -1 ].message && data[data.length -1 ].message.createdAt=== null){
      //   data.pop();
      //   state = state.set("data",data);
      // }
      state = state.set('selectedConversationInfo', conversationsInitialState.selectedConversationInfo);
      return state.set('selectedPeerVcard', null).set('isSelectingPeerVCard', false).set('dataPreVCard', null);
    }

    case types.UPDATE_CONVERSATION_SUCCESS: {
      if (action.chatMessage) {
        // let selectedVcardXmppId = state.selectedPeerVcard ? state.selectedPeerVcard.xmppId : '';
        // let bySelf = (action.chatMessage.from === "") ? true : false;
        // let messageTime = action.chatMessage.time;
        // let items = state.data.map(value => {
        //   if (value.peerVcard.xmppId === action.peerXpppId) {
        //     let updatedConversation = cloneDeep(value);
        //     if(bySelf){
        //     updatedConversation.lastUserSentTime = messageTime;
        //     } else {
        //     updatedConversation.lastBuddySentTime = messageTime;
        //     }
        //     if (!selectedVcardXmppId || selectedVcardXmppId !== action.peerXpppId) {
        //       updatedConversation.unreadCount += action.unreadCountIncrement || 0;
        //     }
        //     if (action.chatMessage) {
        //       updatedConversation.message = cloneDeep(action.chatMessage);
        //       updatedConversation.message.body.data = messageUtils.getInboxMessageContent(action.chatMessage, selectedVcardXmppId);
        //       updatedConversation.message.user = null;
        //     }
        //     // else {
        //     //   updatedConversation.message.body.data = '';
        //     // }
        //     return updatedConversation;
        //   }
        //   else return value;
        // });
        let newConversations: Conversation[] = [];
        const newUnpinnedConversations: Conversation[] = [];
        const newPinnedConversations: Conversation[] = [];
        const newDiamondConversations: Conversation[] = [];
        const newGiftConversations: Conversation[] = [];
        let pinMatchCount = 0;
        // update unread Conversation
        let { unreadConversations, unreadMatchingConversations } = state;
        // ---/
        const conversations = Immutable.asMutable(state.data);
        let selectedConversation = null;
        if (conversations && conversations.length > 0) {
          for (let i = 0; i < conversations.length; i++) {
            if (conversations[i].peerVcard.xmppId === action.peerXpppId) {
              selectedConversation = conversations[i];
            } else {
              if (conversations[i].pinned) {
                newPinnedConversations.push(conversations[i]);
              } else if (
                conversations[i]?.hasUnreadGifts ||
                conversations[i]?.buddyBoostExpiryAt >= new Date().getTime() + state.chatServerTime
              ) {
                newGiftConversations.push(conversations[i]);
              } else if (isDiamondConversationShouldBeSort(conversations[i])) {
                newDiamondConversations.push(conversations[i]);
              } else {
                newUnpinnedConversations.push(conversations[i]);
              }
            }
          }
          const firstConversation = updateConversation(
            selectedConversation,
            state.selectedPeerVcard,
            action.peerXpppId,
            action.chatMessage,
            action.chatMessage.from,
            action.chatMessage.time,
            action.unreadCountIncrement,
            action.isForwardedMessage,
            action.xmppId
          );

          if (firstConversation && firstConversation.unreadCount === 1 && !firstConversation.isIgnored) {
            unreadConversations += 1;
            if (firstConversation.peerVcard.isMatch) {
              unreadMatchingConversations += 1;
            }
          }

          if (firstConversation && firstConversation.pinned) {
            newPinnedConversations.unshift(firstConversation);
          } else if (
            firstConversation?.hasUnreadGifts ||
            (firstConversation?.buddyBoostExpiryAt && firstConversation.buddyBoostExpiryAt >= new Date().getTime() + state.chatServerTime)
          ) {
            newGiftConversations.unshift(firstConversation);
          } else if (isDiamondConversationShouldBeSort(firstConversation!)) {
            newDiamondConversations.unshift(firstConversation!);
          } else if (firstConversation) {
            newUnpinnedConversations.unshift(firstConversation);
          }
          newConversations = [
            ...newPinnedConversations,
            ...sortGiftConversations(newGiftConversations),
            ...newDiamondConversations,
            ...newUnpinnedConversations,
          ];
          newPinnedConversations.forEach(e => {
            if (e.peerVcard.isMatch) {
              pinMatchCount++;
            }
          });
        }
        const lastUpdateConversationsDataTime = new Date().getTime();
        return state
          .set('data', newConversations)
          .set('unreadConversations', unreadConversations)
          .set('unreadMatchingConversations', unreadMatchingConversations)
          .set('lastUpdateConversationsDataTime', lastUpdateConversationsDataTime)
          .set('pinMatchCount', pinMatchCount)
          .set('conversationLocation', countAllLocationOfConversation(newConversations));
      }
      // console.log("lastUpdateConversationsDataTime", lastUpdateConversationsDataTime)
      return state;
    }

    case types.RESET_CONVERSATIONS: {
      // Update to do not reset conversationKey (to check issue empty conversationKey when send notify message RVC-3761)
      // TODO: Should update this logic when resolve RVC-3761
      return state.merge({
        ...conversationsInitialState,
        selectedConversationInfo: {
          ...conversationsInitialState.selectedConversationInfo,
          conversationKey: state.selectedConversationInfo.conversationKey,
        },
      });
    }

    case types.DELETE_CONVERSATION_LAST_MESSAGE_SUCCESS: {
      const items = Immutable.asMutable(state.data).map(value => {
        if (
          value.peerVcard.xmppId === action.peerXpppId &&
          (value.message.bySelf || value.message.from === '' || value.message.from == action.peerXpppId)
        ) {
          const updatedConversation = cloneDeep(value);
          if (updatedConversation.message.id === action.msgId) {
            updatedConversation.message.isDelete = true;
          }
          // console.log("updatedConversation", updatedConversation)
          return updatedConversation;
        }
        return value;
      });
      return state
        .set('data', items)
        .set('lastUpdateConversationsDataTime', new Date().getTime())
        .set('conversationLocation', countAllLocationOfConversation(items));
    }

    case types.RESET_CONVERSATION_UNREAD_COUNT_SUCCESS: {
      const targetId = action.selectedPeerVcard.xmppId;
      const currentTimestamp = new Date().getTime();

      // 1. Locate the conversation safely
      const targetIndex = state.data.findIndex((c: Conversation) => c.peerVcard.xmppId === targetId);
      if (targetIndex === -1) return state;

      const originalConv = state.data[targetIndex];

      // We will build the 'next version' of this conversation step-by-step,
      // exactly matching your original if/then logic.
      let nextConv = originalConv;
      let hasChanged = false;

      // --- LOGIC STEP A: Handle Gifts ---
      // Original Code: If hasUnreadGifts, set it false, update state (conceptually), but CONTINUE.
      if (nextConv.hasUnreadGifts) {
        nextConv = nextConv.set('hasUnreadGifts', false);
        hasChanged = true;
      }

      // --- LOGIC STEP B: Check Unread Count ---
      // Original Code: If unreadCount is 0, return current state (with gift changes if any).
      if (nextConv.unreadCount === 0) {
        if (hasChanged) {
          // Logic: User had unread gifts (now cleared) but 0 unread messages.
          // We must return the state with the gift update, but NO sorting.
          const newData = state.data.asMutable();
          newData[targetIndex] = nextConv;
          return state.merge({
            data: newData,
            lastUpdateConversationsDataTime: currentTimestamp,
            conversationLocation: countAllLocationOfConversation(newData as any),
          });
        }
        return state; // No gifts changed, no messages changed. Do nothing.
      }

      // --- LOGIC STEP C: Clear Unread & Update Counters ---
      // If we are here, we know unreadCount > 0.
      let { unreadConversations, unreadMatchingConversations } = state;

      // Update Counters
      if (!nextConv.isIgnored) {
        unreadConversations -= 1;
        if (nextConv.peerVcard.isMatch) {
          unreadMatchingConversations -= 1;
        }
      }

      // Clear Count on the object
      nextConv = nextConv.set('unreadCount', 0);

      // --- LOGIC STEP D: Non-Diamond Handling (Early Exit) ---
      // Original Code: If NOT Diamond, return early. Do NOT sort.
      if (nextConv.memberLevel < MEMBER_TYPES.PLATINUM_DIAMOND) {
        const newData = state.data.asMutable();
        newData[targetIndex] = nextConv;
        return state.merge({
          data: newData,
          unreadConversations,
          unreadMatchingConversations,
          lastUpdateConversationsDataTime: currentTimestamp,
          conversationLocation: countAllLocationOfConversation(newData as any),
        });
      }

      // --- LOGIC STEP E: Diamond Handling (Full Re-Sort) ---
      // Original Code: If Diamond, fall through to the loop/sorting logic.

      const pinnedConversations: Conversation[] = [];
      const diamondConversations: Conversation[] = [];
      const giftConversations: Conversation[] = [];
      let unPinnedConversations: Conversation[] = [];

      // We iterate existing data to bucketize, BUT we swap in our 'nextConv' for the target.
      state.data.forEach((conv, i) => {
        // If this is the target, use our updated version (gifts cleared, unread cleared)
        // If not, use the existing immutable object
        const currentConv = i === targetIndex ? nextConv : conv;

        if (i === targetIndex) {
          // SPECIAL TARGET LOGIC from original code:
          // The target NEVER goes into 'gift' or 'diamond' buckets in the original loop.
          // It only goes to pinned or unPinned.
          if (currentConv.pinned) {
            pinnedConversations.push(currentConv.asMutable({ deep: true })); // Cast if needed for TS
          } else {
            unPinnedConversations.push(currentConv.asMutable({ deep: true }));
          }
        } else {
          // STANDARD LOGIC for everyone else
          if (currentConv.pinned) {
            pinnedConversations.push(currentConv.asMutable({ deep: true })); // Cast if needed for TS
          } else if (
            currentConv.hasUnreadGifts ||
            (currentConv.buddyBoostExpiryAt && currentConv.buddyBoostExpiryAt >= currentTimestamp + state.chatServerTime)
          ) {
            giftConversations.push(currentConv.asMutable({ deep: true })); // Cast if needed for TS
          } else if (isDiamondConversationShouldBeSort(currentConv.asMutable({ deep: true }))) {
            diamondConversations.push(currentConv.asMutable({ deep: true })); // Cast if needed for TS
          } else {
            unPinnedConversations.push(currentConv.asMutable({ deep: true })); // Cast if needed for TS
          }
        }
      });

      // Sort and Combine
      unPinnedConversations = sortConversationByMessageTimeArray(unPinnedConversations);

      const updatedActionData = [
        ...pinnedConversations,
        ...sortGiftConversations(giftConversations),
        ...diamondConversations,
        ...unPinnedConversations,
      ];

      return state.merge({
        data: updatedActionData,
        unreadConversations,
        unreadMatchingConversations,
        lastUpdateConversationsDataTime: currentTimestamp,
        conversationLocation: countAllLocationOfConversation(updatedActionData),
      });
    }

    case types.SET_READ_CONVERSATION_FAILURE:
      return state.set('isSetReadConversationError', true).set('isSetReadConversationLoading', false);

    case types.SET_READ_CONVERSATION_SUCCESS:
      return state.set('isSetReadConversationError', false).set('isSetReadConversationLoading', false);

    case types.SET_READ_CONVERSATION_REQUEST:
      return state.set('isSetReadConversationLoading', true);

    case types.BLOCK_USER: {
      const updateData = state.data.filter(x => x.peerVcard.userId.toString() !== action.data.toString());
      return state
        .set('data', updateData)
        .set('lastUpdateConversationsDataTime', new Date().getTime())
        .set('conversationLocation', countAllLocationOfConversation(updateData.asMutable({ deep: true })));
    }

    case types.FILTER_CONVERSATIONS: {
      const { keyword, secondaryIndex, primaryIndex, isMatchDefault } = action.data;

      state = state.set('pageNumber', 1);
      let isSearched = true;
      if (!isMatchDefault && secondaryIndex === 0 && primaryIndex === 0) {
        isSearched = false;
      } else if (isMatchDefault && secondaryIndex === 0 && primaryIndex === 1) {
        isSearched = false;
      } else if (isMatchDefault && secondaryIndex === 0 && primaryIndex === 0) {
        isSearched = false;
      }
      return state.set('search', {
        keyword,
        primaryIndex: action.data.primaryIndex,
        secondaryIndex,
        isSearched,
      });
    }

    case types.UPDATE_FILTER_PARAMS_BY_DEFAULT_FOLDER: {
      state = state.set('search', {
        keyword: '',
        primaryIndex: action.data === DEFAULT_FOLDER_OPTION.ALL ? 0 : 1,
        secondaryIndex: 0,
        isSearched: false,
      });
      return state;
    }

    case types.PIN_CONVERSATION_SUCCESS: {
      const { pinListLength } = state;
      let { pinMatchCount } = state;
      let pinnedConversations: Conversation[] = [];
      const unpinnedConversations: Conversation[] = [];
      let updatedData = [];
      state.data.forEach(value => {
        if (value.peerVcard.xmppId.toString() === action.data.toString()) {
          const updatedValue = Object.assign({}, value.asMutable({ deep: true }));
          updatedValue.pinned = true;
          pinnedConversations.push(updatedValue);
          if (updatedValue.peerVcard.isMatch) {
            pinMatchCount += 1;
          }
        } else {
          if (value.pinned) {
            pinnedConversations.push(value.asMutable({ deep: true }));
          } else {
            unpinnedConversations.push(value.asMutable({ deep: true }));
          }
        }
      });
      //      pinnedConversations = sortConversationByMessageTimeArray(pinnedConversations);
      updatedData = [...pinnedConversations, ...unpinnedConversations];
      return state
        .setIn(['selectedConversationInfo', 'pinned'], true)
        .setIn(['pinListLength'], pinListLength + 1)
        .set('data', sortConversations(updatedData, [], state.chatServerTime).data)
        .set('pinMatchCount', pinMatchCount)
        .set('lastUpdateConversationsDataTime', new Date().getTime());
    }

    case types.UNPIN_CONVERSATION_SUCCESS: {
      const { pinListLength } = state;
      let { pinMatchCount } = state;
      const pinnedConversations: Conversation[] = [];
      let unpinnedConversations: Conversation[] = [];
      let updatedData = [];
      state.data.forEach(value => {
        if (value.peerVcard.xmppId.toString() === action.data.toString()) {
          const updatedValue = Object.assign({}, value.asMutable({ deep: true }));
          updatedValue.pinned = false;
          unpinnedConversations.push(updatedValue);
          if (value.peerVcard.isMatch && pinMatchCount > 0) {
            pinMatchCount -= 1;
          }
        } else {
          if (value.pinned) {
            pinnedConversations.push(value.asMutable({ deep: true }));
          } else {
            unpinnedConversations.push(value.asMutable({ deep: true }));
          }
        }
      });
      //      unpinnedConversations = sortConversationByMessageTimeArray(unpinnedConversations);
      updatedData = [...pinnedConversations, ...unpinnedConversations];
      return state
        .setIn(['selectedConversationInfo', 'pinned'], false)
        .setIn(['pinListLength'], pinListLength - 1)
        .set('data', sortConversations(updatedData, [], state.chatServerTime).data)
        .set('pinMatchCount', pinMatchCount)
        .set('lastUpdateConversationsDataTime', new Date().getTime());
    }

    case types.CHECK_PIN_STATUS_CONVERSATION_SELECTED: {
      const { selectedPeerVcard } = state;
      if (selectedPeerVcard) {
        const conversation = state.data.filter(item => item.pinned && item.peerVcard.userId === selectedPeerVcard.userId);
        const isPinned = conversation && conversation.length > 0;
        return state.setIn(['selectedConversationInfo', 'pinned'], isPinned);
      }
      return state;
    }

    case types.UPDATE_MODAL_DELETE: {
      return state.set('showDelete', action.data);
    }

    case types.UPDATE_LOADING_CONVERSATION_DETAIL: {
      const arrLoadingItem = {
        isLoading: action.data.isLoading,
        msgId: action.data.msgId,
        percent: action.data.percent,
        to: action.data.to,
        isVideoMp4File: action.data.isVideoMp4File,
        isAudio: action.data.isAudio,
      };

      let existingArrLoading;
      const existingPeerVcard = state.uploading.filter(x => x.selectedPeerVcard === action.data.to);
      if (existingPeerVcard && existingPeerVcard[0]) {
        existingArrLoading = existingPeerVcard[0].arrLoading;
      }

      if (existingArrLoading && existingArrLoading.length > 0) {
        existingArrLoading = existingArrLoading.filter((x: any) => x.msgId !== arrLoadingItem.msgId);
        const newArrLoading = existingArrLoading.asMutable() || [];
        newArrLoading.push(arrLoadingItem);
        existingArrLoading = newArrLoading;
      } else {
        existingArrLoading = [arrLoadingItem];
      }

      const updatedLoading = state.uploading.asMutable().filter(x => x.selectedPeerVcard !== action.data.to);

      updatedLoading.push({
        selectedPeerVcard: action.data.to,
        arrLoading: existingArrLoading,
      });
      state = state.set('uploading', updatedLoading);

      return state;
    }

    case types.GET_CONVERSATION_STATUS_FAILURE:
      return state.setIn(['selectedConversationInfo', 'isError'], true).setIn(['selectedConversationInfo', 'isLoading'], false);

    case types.GET_CONVERSATION_STATUS_REQUEST:
      return state.setIn(['selectedConversationInfo', 'isLoading'], true);

    case types.UPDATE_LASTTIME_SYNC:
      return state.set('lastSyncTime', action.data.lastSyncTime);

    case types.UPDATE_SERVER_TIME_GROUP_CHAT_SUCCESS:
      return state.setIn(['selectedConversationInfo', 'diffTimeGroup'], action.data);

    case types.ADD_DATA_TO_SCHEDULE_REMOVE_CONVERSATION:
      return state.set('removingConversationData', { curNode: action.curNode, isGroup: action.isGroup });
    case types.CLEAR_DATA_TO_SCHEDULE_REMOVE_CONVERSATION:
      return state.set('removingConversationData', null);
    case types.GET_CONVERSATION_STATUS: {
      const extentObj: Partial<ISelectedConversationInfo> = {
        editorStatus: action.data.status,
        remainingMessage: action.data.remaining,
        canSendPhotos: action.data.canSendPhotos,
        remainingCredits: action.data.remainingCredits,
        canVideoCall: action.data.canVideoCall,
        canSend: action.data.canSend,
        reason: action.data.reason,
        canSendPrivate: action.data.canSendPrivate,
        isLoading: false,
        isError: false,
        memberLevelUserLogin: action.data.memberLevelUserLogin,
        recvMemberLevel: action.data.receiverMemberLevel,
        isRecvMatching: action.data.isRecvMatching,
        isSenderMatching: action.data.isSenderMatching,
        timeToReset: action.data.timeToReset,
        // conversationKey: action.data.conversationKey,
        upgradeAction: action.data.upgradeAction,
        isCreditSend: action.data.isCreditSend,
        timeUpdateConversationStatus: +new Date(),
        canSendGifts: action.data.canSendGifts,
        messageBanExpiry: action.data.messageBanExpiry,
        sendPhotosBanExpiry: action.data.sendPhotosBanExpiry,
        diffTime: action.data.diffTime,
      };

      // Check this condition to make sure not replace empty conversationKey to reducer.
      // Accept using previous key (to check issue empty conversationKey when send notify message RVC-3761)
      // TODO: Should update this logic when resolve RVC-3761
      if (action.data.conversationKey) {
        extentObj.conversationKey = action.data.conversationKey;
      }

      const baseInfo = Object.assign({}, state.selectedConversationInfo, extentObj);
      return state.set('selectedConversationInfo', baseInfo);
    }
    case types.UPDATE_MEMBER_LEVEL_CONVERSATION_SELECTED: {
      const conversations = Immutable.asMutable(state.data);
      let newConversation = [];
      const existingConversation = conversations.find(x => x.peerVcard && x.peerVcard.xmppId === action.data.xmppId);
      if (existingConversation) {
        if (existingConversation.memberLevel === action.data.memberLevel) {
          return state;
        }
        const updateConversation = Object.assign({}, existingConversation);
        updateConversation.memberLevel = action.data.memberLevel;
        newConversation = conversations.map(x => {
          if (x.peerVcard.xmppId === action.data.xmppId) {
            return updateConversation;
          }
          return x;
        });
        return state.set('data', newConversation).set('lastUpdateConversationsDataTime', new Date().getTime());
      }
      return state;
    }
    case types.LOAD_MORE_CONVERSATIONS: {
      let { pageNumber } = state;
      // if(pageNumber * PAGING_DEFAULT.PAGE_SIZE_CONVERSATION <=  action.data){
      pageNumber++;
      // }
      return state.set('pageNumber', pageNumber);
    }

    case types.RESET_PAGE_NUMBER_CONVERSATIONS: {
      return state.set('pageNumber', conversationsInitialState.pageNumber);
    }

    case types.UPDATE_CONVERSATION_NAVIGATE_TYPE: {
      return state.set('showUnreadCount', action.data);
    }

    case types.CHECK_MATCHING_PROFILE_SUCCESS: {
      return state.setIn(['selectedConversationInfo', 'matched'], action.data);
    }
    case types.USER_LOGOUT_SUCCESS: {
      state = conversationsInitialState;
      return state;
    }

    case types.LOAD_MAIN_AVATARS_SUCCESS: {
      const { data } = action;
      const items = Immutable.asMutable(state.data).map(value => {
        const find = data.find((e: User) => {
          return e.userId === value.peerVcard.userId;
        });
        if (find) {
          const updatedConversation = cloneDeep(value);
          if (find.userId) {
            const peerVCard = cloneDeep(value.peerVcard);
            peerVCard.avatarUrl = find.data.thumbUrl;
            updatedConversation.peerVcard = peerVCard;
          }
          return updatedConversation;
        }
        return value;
      });

      // TODO: asdasd
      return state.set('data', items).set('lastUpdateConversationsDataTime', new Date().getTime());
    }
    case types.UPDATE_CONVERSATION: {
      return state.merge({ ...action.data });
    }
    case types.UPDATE_STATUS_CONVERSATION: {
      let data = [...state.data.asMutable({ deep: true })];
      if (state.selectedPeerVcard && state.selectedPeerVcard.userId === action.userId) {
        if (data.length > 0) {
          data = data.map(conv => {
            if (conv.peerVcard.userId === action.userId) {
              const peerNew = { ...conv.peerVcard };
              // peerNew.online = action.online;
              return Object.assign({}, { ...conv }, { peerVcard: peerNew });
            }
            return conv;
          });
          state = state
            .set('data', data)
            .set('lastUpdateConversationsDataTime', new Date().getTime())
            .set('conversationLocation', countAllLocationOfConversation(data));
        }
        state = state.set('selectedPeerVcard', Object.assign({}, { ...state.selectedPeerVcard }));
      }

      return state;
    }
    case types.SAVE_PEEVCARD: {
      return state.set('dataPreVCard', action.data);
    }

    case types.UPDATE_LIKE_OR_FAVORITE_MUTAL: {
      state = state.setIn(['selectedPeerVcard', 'isFavoritedMutual'], action.isFavoritedMutual);
      state = state.setIn(['selectedPeerVcard', 'isLikedMutual'], action.isLikedMutual);
      return state;
    }

    case types.UPDATE_SELECT_PEER_C_CARD_STATUS: {
      return state.set('isSelectingPeerVCard', action.data);
    }
    case types.UPDATE_DRAFT_MSG: {
      const data = [...state.listMsgDraft.asMutable({ deep: true })];
      const newListMsgDraft: any = [];
      const hasConversation = data.findIndex(i => i.id === action.data.id);
      if (hasConversation !== -1 && data) {
        data.forEach(i => {
          const item = { ...i };
          if (i.id === action.data.id) {
            item.msgDraft = action.data.msgDraft;
          }
          newListMsgDraft.push(item);
        });
        return state.set('listMsgDraft', newListMsgDraft);
      }
      data.push(action.data);
      return state.set('listMsgDraft', data);
    }

    case types.HIDDEN_CONVERSATION_LAST_MESSAGE_SUCCESS: {
      const indexItemConversations = state.data.findIndex(conv => conv.id === action.id);
      if (indexItemConversations !== -1) {
        return state
          .setIn(['data', indexItemConversations, 'message', 'expts'], 1)
          .set('lastUpdateConversationsDataTime', new Date().getTime());
      }
      return state;
    }

    case types.UPDATE_LIST_CONVERSATION_IDS_DELETED: {
      return state.set('listConversationIdsDeleted', action.data);
    }

    case types.PATCH_IGNORE_CONVERSATION_SUCCESS:
      let _pinListLength = state.pinListLength;
      const updatedConversation = state.data.asMutable().map(c => {
        const conversation = { ...c.asMutable({ deep: true }) };
        if (conversation.id === action.id) {
          conversation.isIgnored = action.isIgnored || null;
          // We remove the pin of this conversation when it has been ignored by the user.
          if (conversation.pinned && action.isIgnored) {
            conversation.pinned = false;
            _pinListLength = _pinListLength - 1;
          }
        }
        return conversation;
      });
      state = state.merge({
        pinListLength: _pinListLength,
      });
      return state.set('data', updatedConversation).set('lastUpdateConversationsDataTime', new Date().getTime());
    case types.PATCH_RULES_GROUP_CHAT_SUCCESS:
    case types.END_GROUP_CHAT_SUCCESS:
      const { id, roomJid } = action.params;
      let { pinListLength, unreadConversations } = Immutable.asMutable(state);
      const data = state.data.asMutable({ deep: true });
      const newConversations = [];
      for (let i = 0; i < data.length; i++) {
        const obj = { ...data[i] };
        // For patch rules modal
        if (id) {
          if (obj.peerVcard.id === id) {
            obj.isNewInvitation = false;
          }
          newConversations.push(obj);
        }
        // For end group chat
        if (roomJid) {
          if (obj.peerVcard.xmppId !== roomJid) {
            newConversations.push(obj);
          } else {
            if (obj.pinned) {
              pinListLength -= 1;
            }
            if (obj.unreadCount > 0) {
              unreadConversations -= 1;
            }
          }
        }
      }

      state = state.merge({
        pinListLength,
        unreadConversations,
        lastUpdateConversationsDataTime: new Date().getTime(),
      });
      return state.set('data', newConversations).set('conversationLocation', countAllLocationOfConversation(newConversations));

    case types.UPDATE_GROUP_CHAT_SHOULD_BE_SELECTED:
      return state.set('groupChatShouldBeSelected', action.data);

    case types.UPDATE_JOINED_PRIVATE_GROUP_CHAT: {
      let roomJidsBeingJoined = [...state.roomJidsBeingJoined.asMutable()];
      if (roomJidsBeingJoined.includes(action.data)) {
        roomJidsBeingJoined = roomJidsBeingJoined.filter(roomJid => roomJid !== action.data);
        const roomJidsJoinedSuccess = [...state.roomJidsJoinedSuccess.asMutable()];
        if (!roomJidsJoinedSuccess.includes(action.data)) {
          roomJidsJoinedSuccess.push(action.data);
        }
        return state.set('roomJidsBeingJoined', roomJidsBeingJoined).set('roomJidsJoinedSuccess', roomJidsJoinedSuccess);
      }
      return state;
    }
    case types.REMOVE_ROOMJID_IN_JOINED_SUCCESS_ROOM_JIDS: {
      const roomJidsJoinedSuccess = [...state.roomJidsJoinedSuccess.asMutable()].filter(r => r !== action.data);
      return state.set('roomJidsJoinedSuccess', roomJidsJoinedSuccess);
    }
    case types.CLEAR_JOINED_SUCCESS_ROOM_JID: {
      return state.set('roomJidsJoinedSuccess', []).set('roomJidsJoinedError', []);
    }
    case types.UPDATE_JOINING_ROOM_JIDS: {
      return state.set('roomJidsBeingJoined', action.roomJids);
    }
    case types.UPDATE_JOINED_ROOM_JIDS_FAILED: {
      const roomJidsJoinedError = [...state.roomJidsJoinedError.asMutable()];
      roomJidsJoinedError.push(action.roomJid!);
      const roomJidsBeingJoined = [...state.roomJidsBeingJoined.asMutable()].filter(r => r !== action.roomJid);

      return state.set('roomJidsJoinedError', roomJidsJoinedError).set('roomJidsBeingJoined', roomJidsBeingJoined);
    }
    case types.UPDATE_ROOM_JID_BEING_JOINED: {
      const roomJidsBeingJoined = [...state.roomJidsBeingJoined.asMutable()];
      if (!roomJidsBeingJoined.includes(action.data)) {
        roomJidsBeingJoined.push(action.data);
      }
      const privateRoomJids = [...state.privateRoomJids.asMutable()];
      if (!privateRoomJids.includes(action.data)) {
        privateRoomJids.push(action.data);
      }
      return state.set('privateRoomJids', privateRoomJids).set('roomJidsBeingJoined', roomJidsBeingJoined);
    }
    case types.UPDATE_MESSAGE_ID_SELECTED:
      if (!state.selectedConversationInfo) {
        return state;
      }
      return state
        .setIn(['selectedConversationInfo', 'msgIdSelected'], action.msgId)
        .setIn(['selectedConversationInfo', 'isEditMessage'], action.isEdit);
    case types.UPDATE_PARTICIPANT_COUNT:
      const newConversation = [...state.data.asMutable({ deep: true })].map(c => {
        const conversation = { ...c };
        if (conversation.peerVcard.xmppId === action.roomJid) {
          conversation.memberCount = action.count!;
        }
        return conversation;
      });
      return state
        .set('data', newConversation)
        .set('lastUpdateConversationsDataTime', new Date().getTime())
        .set('conversationLocation', countAllLocationOfConversation(newConversation));
    case types.TOGGLE_AUTO_SELECT_CONVERSATION:
      return state.set('isAutoSelectFirstConversation', action.toggle);
    case types.UPDATE_MEMBER_COUNT:
      const indexData = state.data.findIndex(c => c.peerVcard.xmppId === action.data.roomJid);

      return state
        .setIn(['data', indexData, 'memberCount'], action.data.memberCount)
        .set('lastUpdateConversationsDataTime', new Date().getTime());
    case types.TOGGLE_OPTION_DATE_FINDER_EVENT:
      const { selectedPeerVcard } = Immutable.asMutable(state);
      return state.merge({
        selectedPeerVcard: {
          ...selectedPeerVcard,
          dateFinderCount: action.data.DateFinderCount,
          eventCount: action.data.EventCount,
        },
      });
    case types.DELETE_FOLDER_SUCCESS:
      return state
        .set(
          'data',
          state.data.map(conversation => {
            const existsFolder = conversation.folders.findIndex(folderId => folderId === action.payload.id);
            return {
              ...conversation,
              folders: conversation.folders.filter(folderId => folderId !== action.payload.id),
              isInboxRetention: existsFolder !== -1 ? true : conversation.isInboxRetention,
            };
          })
        )
        .set('lastUpdateConversationsDataTime', new Date().getTime());

    case types.UPDATE_CONVERSATION_GROUP_COVER_URL:
      const { convId, coverUrl } = action.data;
      for (let i = 0; i < state.data.length; i++) {
        if (state.data[i].id === convId && state.data[i].peerVcard) {
          state = state.setIn(['data', i, 'peerVcard', 'avatarUrl'], coverUrl).setIn(['data', i, 'peerVcard', 'isExistsAvatar'], true);
          break;
        }
      }
      return state.set('lastUpdateConversationsDataTime', new Date().getTime());

    case types.UPDATE_CHAT_SERVER_TIME:
      return state.set('chatServerTime', action.data);

    case types.UPDATE_CONVERSATION_USER_BOOST_EXPIRY_TIME:
      for (let i = 0; i < state.data.length; i++) {
        if (state.data[i].id === action.data.convId && state.data[i].peerVcard) {
          state = state.setIn(['data', i, 'userBoostExpiryAt'], action.data.time);
          break;
        }
      }
      return state.set('lastUpdateConversationsDataTime', new Date().getTime());
    case types.UPDATE_CONVERSATION_BUDDY_BOOST_EXPIRY_TIME:
      for (let i = 0; i < state.data.length; i++) {
        if (state.data[i].id === action.data.convId && state.data[i].peerVcard) {
          state = state.setIn(['data', i, 'buddyBoostExpiryAt'], action.data.time);
          break;
        }
      }

      return state
        .set('data', sortConversations(state.data.asMutable({ deep: true }), [], state.chatServerTime).data)
        .set('lastUpdateConversationsDataTime', new Date().getTime());
    case types.UPDATE_CONVERSATION_SEARCH: {
      return state.merge({ ...action.data });
    }
    case types.UPDATE_STATE_CONVERSATION_SEARCH: {
      state = state.setIn([...action.dataKey!], action.data);
      return state;
    }
    case types.GET_LIST_ENTITIES_SUCCESS: {
      const { filters, defaultFilters } = state;
      let updateObject = {};
      if (action.categoryId === CATEGORY_ID.MS_SHOW_ME_MEMBER_TYPE && !filters.isHasChangeFilter) {
        updateObject = {
          filters: {
            ...filters,
            showMeMemberType: {
              contentTabs: [
                {
                  data: action.data.items,
                },
              ],
            },
          },
          defaultFilters: {
            ...defaultFilters,
            showMeMemberType: {
              contentTabs: [
                {
                  data: action.data.items,
                },
              ],
            },
          },
        };
      }
      return state.merge({ ...updateObject });
    }
    case types.SHOW_ALERT_CONVERSATIONS_FILTER: {
      return state.set('showAlertFilter', action.data);
    }
    case types.UPDATE_USERS_ONLINE_STATUS: {
      let _data = JSON.parse(JSON.stringify(state.data));
      _data.forEach((conv: Conversation) => {
        if (action.data[conv.peerVcard.id]) {
          conv.peerVcard.online = action.data[conv.peerVcard.id].online;
          conv.peerVcard.isOnlineUpdateTime = new Date();
        }
      });
      state = state.set('data', _data).set('lastUpdateConversationsDataTime', new Date().getTime());
      return state;
    }

    case types.CHAT_EDIT_LAST_MESSAGE_CONVERSATION_SUCCESS: {
      const indexConv = state.data.findIndex(c => c.id === action.convId);
      if (indexConv === -1) return state;
      const newData = [...state.data.asMutable({ deep: true })];
      const conv = newData[indexConv];
      newData[indexConv] = { ...conv, message: { ...conv.message, body: { ...conv.message.body, data: action.message?.body.data } } };
      return state.set('data', newData).set('lastUpdateConversationsDataTime', new Date().getTime());
    }
    default:
      return state;
  }
}

function updateConversation(
  editedConversation: Conversation | null,
  selectedPeerVcard: any,
  peerXpppId: string | number | undefined,
  chatMessage: any,
  messageFrom: any,
  messageTime: any,
  unreadCountIncrement?: number,
  isForwardedMessage?: boolean,
  xmppId?: string | number
): Conversation | null {
  try {
    const conversation = cloneDeep(editedConversation);
    if (conversation === null) {
      return null;
    }
    const selectedVcardXmppId = selectedPeerVcard ? selectedPeerVcard.xmppId : '';
    const bySelf = messageFrom === '' || chatMessage.bySelf || chatMessage.from === xmppId;
    if (bySelf) {
      conversation.lastUserSentTime = messageTime;
    } else {
      conversation.lastBuddySentTime = messageTime;
    }
    if (!selectedVcardXmppId || selectedVcardXmppId !== peerXpppId) {
      if (conversation && conversation.message.id !== chatMessage.id) {
        if (!isGroupMessageStatus(chatMessage)) {
          conversation.unreadCount += unreadCountIncrement || 0;
        }

        if (conversation.isGroup || conversation.unreadCount > 0) {
          conversation.isDelete = false;
        }
        if (isForwardedMessage) {
          conversation.unreadCount = 0;
        }

        if (chatMessage.body?.type === TYPE_GIFT && !bySelf) {
          conversation.hasUnreadGifts = true;
          conversation.giftLevel = chatMessage.body?.data?.level || GiftLevel.Level1;
          conversation.giftValue = chatMessage.body?.data?.price || 0;
        }
      }
    }
    if (chatMessage) {
      conversation.message = cloneDeep(chatMessage);
      if (isGroupMessageStatus(chatMessage)) {
        conversation.message.body.data.value = messageUtils.getInboxMessageContent(chatMessage, selectedVcardXmppId);
      } else {
        conversation.message.body.data = messageUtils.getInboxMessageContent(chatMessage, selectedVcardXmppId);
      }
      conversation.message.user = null;
      if (!bySelf) {
        conversation.memberLevel = chatMessage.body.senderMemberLevel;
        conversation.isRecvMatching = chatMessage.body.isRecvMatching === 1 || chatMessage.body.isRecvMatching === true;
        if (!conversation.isGroup) {
          // only check isMatch with conversation private single chat
          conversation.peerVcard.isMatch = chatMessage.body.isRecvMatching === 1 || chatMessage.body.isRecvMatching === true;
        }
        // conversation.isActive = true;
        if (conversation.lastUserSentTime && !conversation.isActive) {
          conversation.isActive = true;
        }
      } else {
        conversation.memberLevel = chatMessage.body.recvMemberLevel;
        // if (!conversation.isActive) {
        //   conversation.isActive = false;
        // }
        if (conversation.lastBuddySentTime && !conversation.isActive) {
          conversation.isActive = true;
        }
      }
    }
    return conversation;
  } catch (e) {
    console.log('Can not update conversation!', e);
    return null;
  }
}

function filter(data: Conversation[], option: number): Conversation[] {
  const resultFilter: Conversation[] = [];
  const { userInfo } = myStore.getStore().getState().loginReducer;
  if (option === 1) {
    data.forEach(value => {
      const index = findIndex(userInfo.matching, (o: User) => {
        return o.id.toString() === value.peerVcard.id.toString();
      });
      if (index !== -1) {
        resultFilter.push(value);
      }
    });
  } else {
    data.forEach(value => {
      const index = findIndex(userInfo.matching, (o: User) => {
        return o.id.toString() === value.peerVcard.id.toString();
      });
      if (index === -1) {
        resultFilter.push(value);
      }
    });
  }
  return resultFilter;
}

function getPinCount(conversations: Conversation[]): number {
  let pinCount = 0;
  if (conversations && conversations.length > 0) {
    for (let i = 0; i < conversations.length; i++) {
      if (conversations[i].pinned) {
        pinCount++;
      }
    }
  }
  return pinCount;
}

function sortConversations(data: Conversation[] = [], matchingList: User[], serverTime?: number): SortConversationsResult {
  const matchingUsers = matchingList;
  let unreadCount = 0;
  let unreadMatchingConversations = 0;
  let pinMatchCount = 0;
  let now = +new Date();
  let pinnedConversations: Conversation[] = [];
  let unPinnedConversations: Conversation[] = [];
  let diamondConversations: Conversation[] = [];
  const giftConversations: Conversation[] = [];
  data.map(x => {
    // Matching , unread logic
    if (matchingUsers.findIndex(id => id.toString() === x.peerVcard.userId.toString()) >= 0) {
      x.peerVcard.isMatch = true;
      if (x.unreadCount > 0 && !x.isIgnored) {
        unreadMatchingConversations++;
        unreadCount++;
      }
      if (x.pinned) {
        pinMatchCount++;
      }
    } else if (x.peerVcard.isMatch) {
      if (x.unreadCount > 0 && !x.isIgnored) {
        unreadMatchingConversations++;
        unreadCount++;
      }
      if (x.pinned) {
        pinMatchCount++;
      }
    } else if (x.unreadCount > 0 && !x.isIgnored) {
      unreadCount++;
    }

    if (x.message && x.message.time && x.message.time > now) {
      x.message.time = now;

      // maintain the order of conversation if their message time is greater than current local time
      // this assumed conversations from the API are sort by time desc
      now -= 1;
    }
    const diffServerTime = serverTime || 0;
    if (x.pinned) {
      pinnedConversations.push(x);
    } else if (x?.hasUnreadGifts || x?.buddyBoostExpiryAt >= new Date().getTime() + diffServerTime) {
      giftConversations.push(x);
    } else if (isDiamondConversationShouldBeSort(x)) {
      diamondConversations.push(x);
    } else {
      unPinnedConversations.push(x);
    }
    return x;
  });
  pinnedConversations = orderBy(pinnedConversations, c => c.timestamp, 'desc');
  diamondConversations = orderBy(diamondConversations, c => c.timestamp, 'desc');
  unPinnedConversations = orderBy(unPinnedConversations, c => c.timestamp, 'desc');
  const newConversationData = [
    ...pinnedConversations,
    ...sortGiftConversations(giftConversations),
    ...diamondConversations,
    ...unPinnedConversations,
  ];

  return {
    data: newConversationData,
    pinnedConversations,
    diamondConversations,
    unPinnedConversations,
    unreadCount,
    unreadMatchingConversations,
    pinMatchCount,
  };
}

function isOverNumberDays(timestamp: number, number?: number): boolean {
  return moment().diff(moment(timestamp), 'days') > (number || 7);
}

function isDiamondConversationShouldBeSort(conversation: Conversation): boolean {
  return (
    conversation &&
    conversation.memberLevel >= MEMBER_TYPES.PLATINUM_DIAMOND &&
    conversation.unreadCount > 0 &&
    !isOverNumberDays(conversation.timestamp, 7)
  );
}

function sortGiftConversations(conversations: Conversation[] = []): Conversation[] {
  return conversations
    ? orderBy(
        orderBy(conversations, c => c.timestamp, 'desc'),
        c => c.giftLevel,
        'desc'
      )
    : [];
}

function countAllLocationOfConversation(conversations: Conversation[]): ConversationLocationMap {
  let res: ConversationLocationMap = {};
  try {
    for (let i = 0; i < conversations.length; i++) {
      const conItm = conversations[i];
      if (conItm) {
        if (!conItm.isGroup && conItm.message?.msgId) {
          if (conItm.peerVcard) {
            const { countryId, stateId } = conItm.peerVcard;
            const currStateId = stateId || -2;
            if (countryId) {
              if (res[countryId]) {
                if (res[countryId][currStateId]) {
                  const existsConversation = res[countryId][currStateId].findIndex((itm: number) => itm === conItm.id);
                  if (existsConversation === -1) {
                    res[countryId].total += 1;
                    res[countryId][currStateId].push(conItm.id);
                  }
                } else {
                  res[countryId] = { ...res[countryId], total: res[countryId].total + 1, [currStateId]: [conItm.id] };
                }
              } else {
                res = { ...res, [countryId]: { total: 1, [currStateId]: [conItm.id] } };
              }
            }
          }
        }
      }
    }
  } catch (err) {
    console.log(err);
  }
  return res;
}
